import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import _cloneDeep from 'lodash-es/cloneDeep'
import _unionBy from 'lodash-es/unionBy'

import type * as models from './models'
import { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    addRecord: store.actions.standalone({
      data: store.actions.Data.asIs<{
        entityId: ents.core.dms.models.Entity.Id
      }>(),
      reducer: ({ state, data: { entityId } }) => ({
        ...state,
        entries: [...state.entries, { entityId, record: {} }],
      }),
    }),
    updateRecord: store.actions.standalone({
      data: store.actions.Data.asIs<{
        record: ents.core.dms.data.Record.Input
      }>(),
      reducer: ({ state, data: { record } }) => {
        const entries = _cloneDeep(state.entries)
        entries[entries.length - 1] = { ...entries[entries.length - 1], record }
        return { ...state, entries }
      },
    }),
    /* eslint-disable sort-keys */
    discardRecord: store.actions.standalone({
      data: store.actions.Data.asIs<{
        entityId: ents.core.dms.models.Entity.Id
      }>(),
      reducer: ({ state, data: { entityId } }) => ({
        ...state,
        entries: state.entries.filter(ent => ent.entityId !== entityId),
      }),
    }),
    submitRecord: store.actions.request({
      input: store.actions.Data.asIs<{
        record: ents.core.dms.data.Record.Input
        entityId: ents.core.dms.models.Entity.Id
      }>(),
      output: store.actions.Data.asIs<void>(),
      reducers: {
        // TODO: Implement loading in request as an improvement
        request: ({ state }): State => state,
        success: ({
          state,
          data: {
            request: { entityId },
          },
        }) => ({
          ...state,
          entries: state.entries.filter(ent => ent.entityId !== entityId),
        }),
        error: ({ state, data: { error } }): State => state,
        cancellation: ({ state }): State => state,
      },
    }),
    // TODO: We will need a lot more information regarding pagination, infinite scrolling etc, added to this action
    loadRecords: store.actions.request({
      input: store.actions.Data.asIs<{
        entityId: ents.core.dms.models.Entity.Id
        /**
         * Only load records with these IDs. When omitted, all records for the
         * entity are loaded.
         */
        recordIds?: readonly ents.core.dms.data.Record.Id[]
        /**
         * When `true`, the request is skipped if all requested `recordIds`
         * (or any records for the entity when `recordIds` is omitted) already
         * exist in the cache.
         *
         * @default false
         */
        cacheFirst?: boolean
      }>(),
      output: store.actions.Data.asIs<{ records: readonly models.Record[] }>(),
      reducers: {
        request: ({ state, data: { entityId, cacheFirst } }) => ({
          ...state,
          lookup: {
            ...state.lookup,
            [entityId]: {
              ...(cacheFirst ? state.lookup[entityId] : undefined),
              lastAccessed: Date.now(),
              loading: true,
            },
          },
        }),
        success: ({
          state,
          data: {
            request: { entityId, recordIds },
            response: { records },
          },
        }) => ({
          ...state,
          lookup: State.evictLookup({
            ...state.lookup,
            [entityId]: {
              complete:
                !recordIds?.length || !!state.lookup[entityId]?.complete,
              lastAccessed: Date.now(),
              loading: false,
              records: _unionBy(
                records,
                state.lookup[entityId]?.records ?? [],
                'id',
              ),
            },
          }),
        }),
        cancellation: ({
          state,
          data: {
            request: { entityId },
          },
        }) => ({
          ...state,
          lookup: {
            ...state.lookup,
            [entityId]: {
              ...state.lookup[entityId],
              lastAccessed: Date.now(),
              loading: false,
            },
          },
        }),
        error: ({
          state,
          data: {
            request: { entityId },
            error,
          },
        }) => ({
          ...state,
          lookup: {
            ...state.lookup,
            [entityId]: { error, lastAccessed: Date.now(), loading: false },
          },
        }),
      },
    }),
    /* eslint-enable sort-keys */
  } as const satisfies store.actions.Config.Constraint<State>
}
